import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { t } from '../lib/i18n';
import { apiClient, ApiError } from '../lib/api-client';
import { useAuth } from '../lib/auth-context';

/**
 * F1-018 — Tela de desafio MFA durante login.
 *
 * Shape do backend:
 *   POST /auth/mfa/verify   body { code } -> 204
 *   POST /auth/mfa/recover  body { recovery_code } -> 204
 *
 * Apos verify OK, hidrata /me e navega para o destino original (ou dashboard).
 */

type Mode = 'code' | 'recovery';

export function MfaChallengePage(): JSX.Element {
  const navigate = useNavigate();
  const { hydrateMe, setMfaVerified } = useAuth();
  const [mode, setMode] = useState<Mode>('code');
  const [code, setCode] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const doSubmit = async (): Promise<void> => {
    setError(null);
    try {
      setSubmitting(true);
      if (mode === 'code') {
        if (!/^\d{6}$/.test(code)) {
          setError(t('mfa.challenge.invalid'));
          return;
        }
        await apiClient.post<void>('/auth/mfa/verify', { code });
      } else {
        if (recoveryCode.trim().length === 0) {
          setError(t('mfa.challenge.invalid'));
          return;
        }
        await apiClient.post<void>('/auth/mfa/recover', {
          recovery_code: recoveryCode.trim(),
        });
      }
      setMfaVerified();
      await hydrateMe();
      navigate('/dashboard', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(t('mfa.challenge.invalid'));
      } else {
        setError(t('common.error'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="auth-page" role="main">
      <section className="auth-card mfa-challenge" aria-labelledby="mfa-challenge-title">
        <h1 id="mfa-challenge-title">{t('mfa.challenge.title')}</h1>
        <p>{t('mfa.challenge.description')}</p>

        <form
          onSubmit={(ev) => {
            ev.preventDefault();
            void doSubmit();
          }}
          noValidate
        >
          {mode === 'code' ? (
            <>
              <label htmlFor="mfa-challenge-code">{t('mfa.challenge.codeLabel')}</label>
              <input
                id="mfa-challenge-code"
                name="code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                pattern="[0-9]{6}"
                required
                value={code}
                placeholder={t('mfa.challenge.codePlaceholder')}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              />
            </>
          ) : (
            <>
              <label htmlFor="mfa-recovery-code">{t('mfa.challenge.recoveryLabel')}</label>
              <p className="muted">{t('mfa.challenge.recoveryHint')}</p>
              <input
                id="mfa-recovery-code"
                name="recovery_code"
                type="text"
                autoComplete="off"
                required
                value={recoveryCode}
                onChange={(e) => setRecoveryCode(e.target.value)}
              />
            </>
          )}

          {error !== null ? (
            <p role="alert" className="error">
              {error}
            </p>
          ) : null}

          <button type="submit" disabled={submitting}>
            {submitting ? t('mfa.challenge.submitting') : t('mfa.challenge.verify')}
          </button>
        </form>

        <button
          type="button"
          className="link-button"
          onClick={() => {
            setError(null);
            setMode((m) => (m === 'code' ? 'recovery' : 'code'));
          }}
        >
          {mode === 'code' ? t('mfa.challenge.useRecovery') : t('mfa.challenge.backToCode')}
        </button>
      </section>
    </main>
  );
}
